<template>
  <Teleport to="body" :disabled="!open">
    <wa-dialog light-dismiss :open="open" without-header @wa-request-close="$emit('close')">
      <div class="wishlist-selection-dialog">
        <h3 class="wishlist-selection-dialog__title">
          {{ $t('wishlist.dialog.selectWishlist.title') }}
        </h3>
        <p class="wishlist-selection-dialog__subtitle">
          {{ $t('wishlist.dialog.selectWishlist.description') }}
        </p>

        <div class="wishlist-selection-dialog__wishlists">
          <div
            v-for="wishlist in wishlists"
            :key="wishlist.id"
            class="wishlist-item"
            :class="{
              'wishlist-item--selected': selectedWishlistIds.includes(wishlist.id),
              'wishlist-item--contains-item': itemCurrentlyInWishlists.includes(wishlist.id),
              'wishlist-item--read-only': wishlistIsReadOnly(wishlist.id)
            }"
            @click="toggleWishlistSelection(wishlist.id)"
          >
            <div class="wishlist-item__info">
              <div class="wishlist-item__name">{{ wishlist.name }}</div>
              <div class="wishlist-item__count">
                {{ wishlist.numberOfItems }} {{ $t('wishlist.items') }}
              </div>
              <div
                v-if="itemCurrentlyInWishlists.includes(wishlist.id)"
                class="wishlist-item__status"
              >
                {{ $t('wishlist.dialog.currentlyInWishlist') }}
              </div>
              <div v-if="wishlistIsReadOnly(wishlist.id)" class="wishlist-item__read-only-hint">
                {{ $t('wishlist.errors.readOnlyHint') }}
              </div>
              <wa-tag
                v-if="wishlist.type"
                size="s"
                :variant="wishlist.type === 'web' ? 'success' : 'brand'"
                style="margin-top: var(--wa-space-xs); font-size: 0.7rem; align-self: flex-start"
              >
                {{ wishlist.type }}
              </wa-tag>
            </div>
            <wa-checkbox :checked="selectedWishlistIds.includes(wishlist.id)" disabled="true" />
          </div>
        </div>

        <div class="wishlist-selection-dialog__create-section">
          <div class="wishlist-selection-dialog__count">
            {{
              $t('wishlist.dialog.currentCount', {
                current: wishlistsList().length,
                max: MAX_WISHLISTS
              })
            }}
          </div>

          <div
            v-if="numOfAvailableWishlists === 0"
            class="wishlist-selection-dialog__limit-reached"
          >
            {{ $t('wishlist.dialog.maxLimitReached') }}
          </div>

          <div v-if="numOfAvailableWishlists > 0" class="wishlist-selection-dialog__create-new">
            <wa-button
              variant="neutral"
              appearance="outlined"
              size="s"
              @click="showCreateForm = !showCreateForm"
            >
              <wa-icon library="bootstrap" slot="start" name="plus"></wa-icon>
              {{ $t('wishlist.dialog.createNew') }}
            </wa-button>
          </div>
        </div>

        <div v-if="showCreateForm" class="wishlist-selection-dialog__create-form">
          <wa-input
            v-model="newWishlistName"
            :placeholder="$t('wishlist.dialog.newWishlistName')"
            class="wishlist-selection-dialog__name-input"
          />
          <div class="wishlist-selection-dialog__color-picker">
            <div
              v-for="color in availableColors"
              :key="color.variant"
              class="color-option"
              :class="{
                'color-option--selected': selectedNewWishlistColor?.variant === color.variant
              }"
              :style="{ backgroundColor: color.slcolor }"
              @click="selectedNewWishlistColor = color"
            ></div>
          </div>
          <wa-button
            variant="brand"
            size="s"
            :disabled="!newWishlistName.trim()"
            :loading="creating"
            @click="createNewWishlist"
          >
            {{ $t('wishlist.dialog.create') }}
          </wa-button>
        </div>
      </div>

      <wa-button slot="footer" variant="neutral" appearance="outlined" @click="$emit('close')">
        {{ $t('globals.cancel') }}
      </wa-button>
      <wa-button
        slot="footer"
        variant="brand"
        :loading="updating"
        @click="updateWishlistSelections"
      >
        {{ $t('wishlist.dialog.updateWishlists') }}
      </wa-button>
    </wa-dialog>
  </Teleport>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useWishlists } from '@/composables/useWishlists.js'
import { useNotification } from '@/composables/useNotification.js'
import { useCustomerStore } from '@/stores/customer'
import { useUiStore } from '@/stores/ui'
import { useI18n } from 'vue-i18n'
import '@awesome.me/webawesome/dist/components/dialog/dialog.js'
import '@awesome.me/webawesome/dist/components/button/button.js'
import '@awesome.me/webawesome/dist/components/input/input.js'
import '@awesome.me/webawesome/dist/components/checkbox/checkbox.js'
import '@awesome.me/webawesome/dist/components/icon/icon.js'
import '@awesome.me/webawesome/dist/components/tag/tag.js'
const {
  wishlistsList,
  wishlistCreate,
  wishlistAddItem,
  wishlistRemoveItem,
  wishlistsGetBySkuCode,
  wishlistGetNextAvailableColor,
  wishlistGetFreeWishlistsCount,
  wishlistIsReadOnly,
  WISHLIST_COLORS,
  MAX_WISHLISTS
} = useWishlists()

const props = defineProps({
  open: {
    type: Boolean,
    default: false
  },
  item: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['close', 'added', 'removed'])

const customerStore = useCustomerStore()
const uiStore = useUiStore()
const notification = useNotification()
const { t } = useI18n()

const wishlists = computed(() => wishlistsList())
const selectedWishlistIds = ref([])
const itemCurrentlyInWishlists = ref([])
const showCreateForm = ref(false)
const newWishlistName = ref('')
const selectedNewWishlistColor = ref(null)
const creating = ref(false)
const updating = ref(false)

const availableColors = computed(() => {
  const usedColors = wishlists.value.map((list) => list.color.variant)
  return WISHLIST_COLORS.filter((color) => !usedColors.includes(color.variant))
})

const numOfAvailableWishlists = computed(() => {
  return wishlistGetFreeWishlistsCount()
})

// Initialize selections when dialog opens
watch(
  () => props.open,
  (isOpen) => {
    if (isOpen) {
      // Check if customer is selected
      if (!customerStore.getCustomerId) {
        emit('close')
        notification.showNotification({
          message: t('wishlist.dialog.customerRequired'),
          type: 'warning'
        })
        uiStore.$patch({
          showCustomerCreationEditDialog: false,
          showCustomerSearchDialog: true
        })
        return
      }

      // Get wishlists that currently contain this item
      const currentWishlists = wishlistsGetBySkuCode(props.item.sku) || []
      itemCurrentlyInWishlists.value = currentWishlists.map((w) => w.id)
      selectedWishlistIds.value = [...itemCurrentlyInWishlists.value]
    }
    // Reset form when dialog closes
    if (!isOpen) {
      showCreateForm.value = false
      newWishlistName.value = ''
      selectedNewWishlistColor.value = null
      selectedWishlistIds.value = []
      itemCurrentlyInWishlists.value = []
    }
  }
)

// Set default color for new wishlist
watch(
  () => showCreateForm.value,
  (show) => {
    if (show && !selectedNewWishlistColor.value) {
      selectedNewWishlistColor.value = wishlistGetNextAvailableColor()
    }
  }
)

function toggleWishlistSelection(wishlistId) {
  // Prevent selection/deselection of read-only wishlists
  if (wishlistIsReadOnly(wishlistId)) {
    notification.showNotification({
      message: t('wishlist.errors.readOnlyHint'),
      type: 'warning'
    })
    return
  }

  const index = selectedWishlistIds.value.indexOf(wishlistId)
  if (index > -1) {
    selectedWishlistIds.value.splice(index, 1)
  } else {
    selectedWishlistIds.value.push(wishlistId)
  }
}

async function createNewWishlist() {
  if (!newWishlistName.value.trim()) return

  creating.value = true
  try {
    const result = await wishlistCreate(
      newWishlistName.value.trim(),
      selectedNewWishlistColor.value
    )

    if (result.success) {
      // Auto-select the newly created wishlist
      selectedWishlistIds.value.push(result.wishlist.id)
      showCreateForm.value = false
      newWishlistName.value = ''
      selectedNewWishlistColor.value = null
    } else {
      // Show error notification for failed creation
      notification.showNotification({
        message: t(result.message),
        type: 'error'
      })
    }
  } catch (error) {
    console.error('Failed to create wishlist:', error)
    notification.showNotification({
      message: t('wishlist.errors.createFailed'),
      type: 'error'
    })
  } finally {
    creating.value = false
  }
}

async function updateWishlistSelections() {
  updating.value = true

  try {
    // Find wishlists to add to (selected but not currently in)
    const wishlistsToAddTo = selectedWishlistIds.value.filter(
      (id) => !itemCurrentlyInWishlists.value.includes(id)
    )

    // Find wishlists to remove from (currently in but not selected)
    const wishlistsToRemoveFrom = itemCurrentlyInWishlists.value.filter(
      (id) => !selectedWishlistIds.value.includes(id)
    )

    // Add to new wishlists
    for (const wishlistId of wishlistsToAddTo) {
      await wishlistAddItem(wishlistId, props.item.sku)
      emit('added', wishlistId)
    }

    // Remove from deselected wishlists
    for (const wishlistId of wishlistsToRemoveFrom) {
      await wishlistRemoveItem(wishlistId, props.item.sku)
      emit('removed', wishlistId)
    }

    emit('close')
  } catch (error) {
    console.error('Failed to update wishlist selections:', error)
  } finally {
    updating.value = false
  }
}
</script>

<style scoped>
.wishlist-selection-dialog {
  &__title {
    margin: 0 0 8px 0;
    font-size: 1.25rem;
    font-weight: 600;
  }

  &__subtitle {
    margin: 0 0 20px 0;
    color: var(--wa-color-neutral-40);
  }

  &__wishlists {
    margin-bottom: 16px;
  }

  &__create-section {
    margin-bottom: 16px;
    padding-top: 16px;
    border-top: 1px solid var(--wa-color-neutral-80);
  }

  &__count {
    font-size: 0.875rem;
    color: var(--wa-color-neutral-30);
    font-weight: 500;
    margin-bottom: 12px;
  }

  &__limit-reached {
    padding: 12px;
    background: var(--wa-color-warning-95);
    border: 1px solid var(--wa-color-warning-80);
    border-radius: var(--wa-border-radius-s);
    color: var(--wa-color-warning-30);
    font-size: 0.875rem;
    font-weight: 500;
    margin-bottom: 12px;
  }

  &__create-form {
    margin-top: 12px;
    padding: 16px;
    background: var(--wa-color-neutral-95);
    border-radius: var(--wa-border-radius-m);
  }

  &__name-input {
    margin-bottom: 12px;
  }

  &__color-picker {
    display: flex;
    gap: 8px;
    margin-bottom: 12px;
  }
}

.wishlist-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border: 1px solid var(--wa-color-neutral-80);
  border-radius: var(--wa-border-radius-m);
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--wa-color-brand-70);
    background: var(--wa-color-brand-95);
  }

  &--selected {
    border-color: var(--wa-color-brand-50);
    background: var(--wa-color-brand-95);
  }

  &--contains-item {
    border-color: var(--wa-color-success-70);
    background: var(--wa-color-success-95);
  }

  &--read-only {
    opacity: 0.6;
    cursor: not-allowed;
    border-color: var(--wa-color-neutral-70);
    background: var(--wa-color-neutral-90);

    &:hover {
      border-color: var(--wa-color-neutral-70);
      background: var(--wa-color-neutral-90);
    }
  }

  &__color {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    border: 2px solid white;
    box-shadow: 0 0 0 1px var(--wa-color-neutral-70);
  }

  &__info {
    flex: 1;
  }

  &__name {
    font-weight: 500;
    margin-bottom: 2px;
  }

  &__count {
    font-size: 0.875rem;
    color: var(--wa-color-neutral-40);
  }

  &__status {
    font-size: 0.75rem;
    color: var(--wa-color-success-40);
    font-weight: 500;
    margin-top: 2px;
  }

  &__read-only-hint {
    font-size: 0.75rem;
    color: var(--wa-color-warning-40);
    font-weight: 500;
    margin-top: 2px;
    font-style: italic;
  }
}

.color-option {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 2px solid white;
  box-shadow: 0 0 0 1px var(--wa-color-neutral-70);
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.1);
  }

  &--selected {
    box-shadow: 0 0 0 2px var(--wa-color-brand-50);
  }
}
wa-tag {
  &::part(content) {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}
</style>
